/**
 * SecurityPostureTab — Canon → Security Posture (iter87.61 Phase 0 · Step 2)
 *
 * Read-only display of every finding from
 * `/app/memory/security_posture_registry.md §1`. Two audiences per the
 * ratified `COMMONS_MAESTRO_CANON_REDESIGN.md §2.1`:
 *   - The agent — "was this vulnerability accepted? what's the trigger
 *     to revisit?"
 *   - The crew — "we thought about this and here's why we're not fixing
 *     it."
 *
 * All dispositions and severity edits happen via
 * `wf.governance.security-finding` (git PR flow) today. Phase 1 Step 5
 * will introduce the Maestro → Security Posture Review sub-tab as the
 * automated writer.
 */
import { useEffect, useMemo, useState } from "react";
import { ShieldAlert, RefreshCw, Filter } from "lucide-react";
import { api } from "@/lib/api";

const DISPOSITIONS = [
    "accepted",
    "investigating",
    "consulting_expert",
    "mitigating",
    "mitigated",
    "not-applicable",
    "retired",
];

const CREW_SEVERITIES = ["critical", "high", "med", "low"];

const DISP_COLORS = {
    accepted:          "bg-zinc-800 text-zinc-300 border-zinc-700",
    investigating:     "bg-amber-900/40 text-amber-200 border-amber-700",
    consulting_expert: "bg-violet-900/40 text-violet-200 border-violet-700",
    mitigating:        "bg-sky-900/40 text-sky-200 border-sky-700",
    mitigated:         "bg-emerald-900/40 text-emerald-200 border-emerald-700",
    "not-applicable":  "bg-zinc-800 text-zinc-400 border-zinc-700",
    retired:           "bg-zinc-900 text-zinc-500 border-zinc-800",
};

const CREW_SEV_COLORS = {
    critical: "bg-rose-600/20 text-rose-300 border-rose-500/60",
    high:     "bg-rose-500/10 text-rose-400 border-rose-500/40",
    med:      "bg-amber-500/10 text-amber-400 border-amber-500/40",
    low:      "bg-zinc-700/30 text-zinc-400 border-zinc-700",
};

const AUD_SEV_COLORS = {
    CRITICAL: "bg-rose-600/20 text-rose-300 border-rose-500/60",
    HIGH:     "bg-rose-500/10 text-rose-400 border-rose-500/40",
    MEDIUM:   "bg-amber-500/10 text-amber-400 border-amber-500/40",
    LOW:      "bg-zinc-700/30 text-zinc-400 border-zinc-700",
    INFO:     "bg-zinc-800 text-zinc-500 border-zinc-800",
};

function Pill({ label, cls, testId }) {
    return (
        <span
            className={`pill text-[9px] font-bold uppercase border ${cls || "bg-zinc-800 text-zinc-300 border-zinc-700"}`}
            data-testid={testId}
        >
            {label}
        </span>
    );
}

function FindingRow({ row }) {
    return (
        <div
            className="border border-zinc-800 rounded-md p-3 text-xs bg-black/40 space-y-2"
            data-testid={`security-posture-row-${row.id}`}
        >
            <div className="flex items-center gap-2 flex-wrap">
                <code className="text-sm font-bold text-white" data-testid={`security-posture-id-${row.id}`}>
                    {row.id}
                </code>
                <Pill
                    label={row.disposition}
                    cls={DISP_COLORS[row.disposition]}
                    testId={`security-posture-disposition-${row.id}`}
                />
                <Pill
                    label={`crew · ${row.crew_severity}`}
                    cls={CREW_SEV_COLORS[row.crew_severity]}
                    testId={`security-posture-crew-severity-${row.id}`}
                />
                <Pill
                    label={`auditor · ${row.auditor_severity}`}
                    cls={AUD_SEV_COLORS[row.auditor_severity?.toUpperCase()]}
                    testId={`security-posture-auditor-severity-${row.id}`}
                />
                <span className="ml-auto text-[9px] text-zinc-600 font-mono">
                    last reviewed: {row.last_reviewed || "—"} · {row.owner || "—"}
                </span>
            </div>
            <div className="text-[12px] text-zinc-200 font-medium">{row.finding}</div>
            <div className="text-[10px] text-zinc-500">
                <span className="font-bold uppercase text-zinc-400">Source:</span> {row.source || "—"}
            </div>
            {row.trigger_to_revisit && (
                <div className="text-[10px] text-amber-300/90">
                    <span className="font-bold uppercase">Trigger to revisit:</span> {row.trigger_to_revisit}
                </div>
            )}
            {row.spawned && (
                <div className="text-[10px] text-sky-300/90">
                    <span className="font-bold uppercase">Spawned:</span> {row.spawned}
                </div>
            )}
            {row.notes && (
                <div className="text-[10px] text-zinc-400 italic border-t border-zinc-900 pt-1.5">
                    {row.notes}
                </div>
            )}
        </div>
    );
}

export default function SecurityPostureTab() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dispFilter, setDispFilter] = useState("all");
    const [sevFilter, setSevFilter] = useState("all");

    const load = () => {
        setLoading(true);
        setError(null);
        api.releaseOpsSecurityPostureRegistry()
            .then(setData)
            .catch((e) => setError(e?.response?.data?.detail || e?.message || "Failed to load"))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const rows = data?.findings || [];
        return rows.filter((r) =>
            (dispFilter === "all" || r.disposition === dispFilter)
            && (sevFilter === "all" || r.crew_severity === sevFilter),
        );
    }, [data, dispFilter, sevFilter]);

    return (
        <div className="space-y-4" data-testid="security-posture-tab">
            <div className="brutal-card p-4 border-2 border-rose-900/40 bg-rose-950/10">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                            <ShieldAlert className="w-4 h-4 text-rose-300" />
                            <h2 className="font-display font-extrabold text-lg uppercase tracking-wide text-white">
                                Security Posture
                            </h2>
                            {data?.counts?.total > 0 && (
                                <span
                                    className="pill text-[9px] font-bold uppercase border bg-zinc-800 text-zinc-300 border-zinc-700"
                                    data-testid="security-posture-total-badge"
                                >
                                    {data.counts.total} finding{data.counts.total === 1 ? "" : "s"}
                                </span>
                            )}
                        </div>
                        <div className="text-xs text-zinc-400">
                            Every security finding raised against Tiny Crew Rivalries and the crew's disposition of it.
                            Read-only here — dispositions flow through <code className="text-zinc-300">wf.governance.security-finding</code> (git PR).
                        </div>
                        <div className="text-[10px] text-zinc-600 mt-1">
                            Source: <code>{data?.source || "app/memory/security_posture_registry.md"}</code>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={load}
                        disabled={loading}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide bg-zinc-900 border border-zinc-700 rounded hover:text-white disabled:opacity-50"
                        data-testid="security-posture-refresh-btn"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </button>
                </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap text-[11px] border border-zinc-800 rounded-md p-2 bg-black/40">
                <Filter className="w-3.5 h-3.5 text-zinc-500" />
                <label className="text-[10px] font-bold uppercase text-zinc-500">Disposition</label>
                <select
                    value={dispFilter}
                    onChange={(e) => setDispFilter(e.target.value)}
                    data-testid="security-posture-filter-disposition"
                    className="brutal-input text-[10px] py-1 px-2 bg-zinc-900 border-zinc-700"
                >
                    <option value="all">All</option>
                    {DISPOSITIONS.map((d) => {
                        const count = data?.counts?.by_disposition?.[d] || 0;
                        return (
                            <option key={d} value={d}>{d} ({count})</option>
                        );
                    })}
                </select>
                <label className="text-[10px] font-bold uppercase text-zinc-500 ml-2">Crew severity</label>
                <select
                    value={sevFilter}
                    onChange={(e) => setSevFilter(e.target.value)}
                    data-testid="security-posture-filter-crew-severity"
                    className="brutal-input text-[10px] py-1 px-2 bg-zinc-900 border-zinc-700"
                >
                    <option value="all">All</option>
                    {CREW_SEVERITIES.map((s) => {
                        const count = data?.counts?.by_crew_severity?.[s] || 0;
                        return (
                            <option key={s} value={s}>{s} ({count})</option>
                        );
                    })}
                </select>
                <span className="ml-auto text-[10px] text-zinc-500">
                    Showing {filtered.length} of {data?.findings?.length || 0}
                </span>
            </div>

            {error && (
                <div className="text-xs text-rose-300 bg-rose-950/40 border border-rose-800 rounded p-3" data-testid="security-posture-error">
                    {error}
                </div>
            )}
            {!data && !error && (
                <div className="text-xs text-zinc-500" data-testid="security-posture-loading">Loading…</div>
            )}

            {data && filtered.length === 0 && (
                <div className="border border-dashed border-zinc-700 rounded-lg p-6 text-center text-xs text-zinc-500" data-testid="security-posture-empty">
                    No findings match this filter.
                </div>
            )}

            {data && filtered.length > 0 && (
                <div className="space-y-2" data-testid="security-posture-rows">
                    {filtered.map((row) => <FindingRow key={row.id} row={row} />)}
                </div>
            )}
        </div>
    );
}
